import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, DispatchProp } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, CircularProgress, SxProps, Theme } from '@mui/material';
import Typography from '@mui/material/Typography';

import color from 'src/assets/_util.scss';
import { Dropdown } from 'src/components/core/Dropdown';
import { HSpacer } from 'src/components/core/Spacer';
import { HStack, VStack } from 'src/components/core/Stack';
import { View } from 'src/components/core/View';
import { useHighlightsPageContext } from 'src/components/Insights/Catalog/HighlightsPageProvider';
import { StatusItems } from 'src/components/Insights/Catalog/status';
import { Button, LinkButton } from 'src/components/Insights/Common/Button';
import CodeChip from 'src/components/Insights/Common/CodeChip';
import PlayButton from 'src/components/PlayButton/PlayButton';
import ScrollingTranscript from 'src/components/ScrollingTranscript/ScrollingTranscript';
import GlobalAudioContext, {
  useSeekAudio,
} from 'src/contexts/GlobalAudioContext';
import { useAnalyticsContext } from 'src/Providers/AnalyticsProvider';
import { selectors as catalogSelectors } from 'src/redux/catalog/catalog-selectors';
import {
  assignPrimarySpeaker,
  markInternal,
  setEntriesStatus,
} from 'src/redux/catalog/catalog-slice';
import { StoreState } from 'src/redux/store';
import { Action, Category, Name } from 'src/types/analytics';
import { User } from 'src/types/auth';
import { Conversation } from 'src/types/conversation';
import {
  AIRequestStatus,
  Catalog,
  CodeTag,
  Entry,
  EntryStatus,
  Participant,
} from 'src/types/insights';
import {
  mapSeekTimeToConversation,
  mapSeekTimeToHighlight,
} from 'src/util/audio';
import { createBasicProvider } from 'src/util/provider';
import { sortCodes } from '../utils/code';
import { SuggestCodesTooltip } from './CatalogPage';
import { useCatalogPageContext } from './CatalogPageProvider';
interface BaseProps {
  entry: Entry;
  onClick?: () => void;
  onChangeCheck?: (checked: boolean, entry: Entry) => void;
  checked?: boolean;
  open?: boolean;
}

type Props = BaseProps & ReturnType<typeof mapStateToProps> & DispatchProp;

const mapStateToProps = (state: StoreState, props: BaseProps) => {
  const conversation = catalogSelectors.getConversationByEntry(
    state,
    props.entry
  );
  return {
    author: catalogSelectors.getAuthorForEntry(state, props.entry),
    codes: catalogSelectors.getCodeTagsForEntry(state, props.entry),
    conversation: conversation,
    demographics: catalogSelectors.getDemographicsForEntry(state, props.entry),
    snippets: catalogSelectors.getHighlightSnippets(
      state,
      props.entry.snippet_ids
    ),
    participants:
      conversation !== undefined
        ? catalogSelectors.getParticipantsForConversation(state, conversation)
        : [],
  };
};

function HighlightItem({
  author,
  codes,
  conversation,
  demographics,
  entry,
  onClick,
  open = false,
  participants,
  snippets,
  checked = false,
  onChangeCheck,
}: Props) {
  const { t } = useTranslation();
  const highlightsPageContext = useHighlightsPageContext();
  const { pendingTagRequests, aiOn } = highlightsPageContext;

  const { audio_start_offset, audio_end_offset, audio_fade_in_out = 1 } = entry;

  const adjustedStartTime = 0;
  const adjustedEndTime =
    audio_end_offset - audio_start_offset + 2 * audio_fade_in_out;
  const seekAudio = useSeekAudio({
    audioUrl: `${window.location.origin}/api/highlights/play/${entry.annotation_id}`,
    startTime: adjustedStartTime,
    endTime: adjustedEndTime,
    meta: conversation,
    id: `highlight-${entry.annotation_id}`,
  });
  const { isPlaying, stop } = useContext(GlobalAudioContext);
  const { seekTime, activatedPlay } = seekAudio;

  // make sure audio is unmounted when leaving the page so it doesn't
  // cause the player to appear on other pages
  useEffect(() => () => stop(), []);

  // Sort Codes
  // Double sort codes by codebook order, then by ai usage
  const sortedCodes = [...codes];
  sortCodes(sortedCodes);
  const appliedHumanCodes = sortedCodes.filter(
    (c) => c.is_applied && !c.is_suggested
  );
  const appliedAiCodes = sortedCodes.filter(
    (c) => c.is_applied && c.is_suggested
  );
  const unappliedAiCodes = aiOn
    ? sortedCodes.filter((c) => !c.is_applied && c.is_suggested)
    : [];
  const tags = [
    ...demographics,
    ...appliedHumanCodes,
    ...appliedAiCodes,
    ...unappliedAiCodes,
  ];
  const hasTags = tags.length > 0;

  // useCallback needed so we don't constantly create new functions causing
  // scrolling transcript to re-render all the time
  const handlePlay = React.useCallback(
    (seekTime?: number) => {
      const adjustedSeekTime = mapSeekTimeToHighlight(
        seekTime,
        audio_start_offset,
        audio_fade_in_out
      );

      activatedPlay(
        seekTime == null ? audio_start_offset : adjustedSeekTime,
        audio_end_offset
      );
    },
    [audio_start_offset, audio_fade_in_out, activatedPlay, audio_end_offset]
  );

  const speakers = useMemo(
    () =>
      entry.participant_ids?.map(
        (id) =>
          participants.find((participant) => participant.id === +id) ?? {
            id: -1,
            name: t('insights.redacted_user'),
            conversation_id: -1,
            demographic_ids: [],
          }
      ) ?? [],
    [entry.participant_ids, participants, t]
  );

  const awaitingSuggestion = pendingTagRequests.some(
    (req) =>
      req.annotation_id === entry.annotation_id &&
      req.ai_request_status === AIRequestStatus.pending
  );

  const padding = open ? 40 : 20;

  // update author to have redacted user name if the author id is -1
  const updatedAuthor =
    (author?.id ?? -1) === -1
      ? { ...author, user_name: t('insights.redacted_user') }
      : author;

  return (
    <LocalProvider
      value={{
        open,
        entry,
        conversation,
        author: updatedAuthor,
        speakers,
        checked,
        padding,
        seekAudio,
        awaitingSuggestion,
        onChangeCheck,
        ...highlightsPageContext,
      }}
    >
      <VStack
        data-testid={`HighlightCard${open ? '-open' : ''}`}
        key={entry.id}
        sx={{
          backgroundColor: color[checked ? 'foraLighterCustom' : 'gray100'],
          borderRadius: '24px',
          border: `1px solid ${color.gray300}`,
          cursor: open ? '' : 'pointer',
          position: 'relative',
          transition: 'all 200ms ease-out',
          overflow: 'auto',
          marginTop: open ? 'auto' : '',
          pointerEvents: 'auto',
          ':hover': {
            boxShadow: open ? '' : '0 0 12px rgba(0, 0, 0, 0.06)',
            transform: open ? '' : 'scale(1.008) translateY(-1px)',
            borderColor: open ? '' : color.gray500,
          },
        }}
      >
        <View
          // hit box. allows us to set a higher index on other clickable elements
          // thus preventing the highlight card from being opened when clicking on them
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            display: open ? 'none' : 'block',
          }}
          onClick={onClick}
        />

        <TitleBar />

        {entry.description && (
          <Typography
            data-testid="highlight_description"
            variant={open ? 'body1' : 'body2'}
            sx={{
              backgroundColor:
                color[checked ? 'foraPurpleCustomLighter' : 'gray300'],
              lineHeight: '21px',
              p: '8px 12px',
              m: `0 ${padding}px`,
              borderRadius: '8px',
              width: 'fit-content',
              maxWidth: '100%',
              wordBreak: 'break-word',
            }}
          >
            {entry.description}
          </Typography>
        )}

        <Player />

        <ScrollingTranscript
          snippets={snippets}
          seekTime={
            // Needed for proper dynamic highlighting
            isPlaying
              ? mapSeekTimeToConversation(
                  seekTime,
                  audio_start_offset,
                  audio_fade_in_out
                )
              : undefined
          }
          filterWordsEnd={audio_end_offset}
          filterWordsStart={audio_start_offset}
          onPlay={handlePlay}
          hideAdditionalInfo={speakers.length === 1}
          hideTimestamp
          embedded={false}
          style={{
            zIndex: 2,
            border: 'none',
            borderRadius: 0,
            background: 'transparent',
            fontSize: open ? '20px' : '',
            overflowY: 'auto',
            minHeight: '108px',
            margin: open ? `24px ${padding}px` : `16px ${padding}px`,
            pointerEvents: 'none',
          }}
          innerStyle={{
            maxHeight: 'unset',
            pointerEvents: 'auto',
          }}
        />

        {hasTags && (
          <HStack
            sx={{
              flexWrap: 'wrap',
              p: `${open ? 18 : 10}px ${padding}px ${open ? 0 : padding}px`,
            }}
          >
            {tags.map((tag) => (
              <View component="span" key={tag.id} sx={{ mr: '6px', mt: '6px' }}>
                <CodeChip
                  code={tag}
                  noCount
                  suggested={(tag as CodeTag).is_suggested ?? false}
                  applied={(tag as CodeTag).is_applied ?? true}
                />
              </View>
            ))}
          </HStack>
        )}

        {open && <ActionBar />}
      </VStack>
    </LocalProvider>
  );
}

export default connect(mapStateToProps)(React.memo(HighlightItem));

// SUBCOMPONENTS

function TitleBar() {
  const {
    open,
    entry,
    author,
    speakers,
    checked,
    padding,
    awaitingSuggestion,
    onChangeCheck,
    t,
  } = useLocalContext();

  const hasStatusBadge =
    ((entry.status && entry.status !== EntryStatus.ToDo) ||
      entry.is_internal) &&
    !open;

  const hasPortalBadge = !open && !!entry.is_internal;

  const generateBadgeStyles = ({
    color,
  }: {
    color: string;
  }): SxProps<Theme> => ({
    border: `1px solid ${color}`,
    textOverflow: 'ellipsis',
    flexShrink: 1,
    overflow: 'hidden',
    color: color,
    ml: 'auto',
    px: '12px',
    py: '3px',
    lineHeight: '18px',
    fontSize: '13px',
    borderRadius: '8px',
  });

  return (
    <HStack
      sx={{
        alignItems: 'center',
        p: `${padding}px ${padding}px ${open ? '28px' : '12px'}`,
        flexWrap: 'wrap',
      }}
    >
      {!open &&
        (awaitingSuggestion ? (
          <CircularProgress size={16} style={{ margin: '0 8px 0 0' }} />
        ) : (
          <Checkbox
            size="small"
            style={{
              color: color[checked ? 'foraPurple' : 'gray650'],
              marginTop: '-9px',
              marginBottom: '-9px',
              marginLeft: '-9px',
            }}
            checked={checked}
            onChange={(e, checked) => onChangeCheck?.(checked, entry)}
            data-testid="highlight-card-checkbox"
          />
        ))}

      <View
        sx={{
          mr: '12px',
          gap: '4px',
          display: 'flex',
          flexDirection: open ? 'column' : 'row',
        }}
      >
        <Typography
          variant={open ? 'body1' : 'body2'}
          color={color.gray700}
          data-testid="highlight_id"
          sx={{ fontSize: open ? '18px' : '' }}
        >
          #{entry.annotation_id}
        </Typography>

        <Typography
          data-testid="highlight_author"
          variant={open ? 'body1' : 'body2'}
          sx={{ fontSize: open ? '18px' : '' }}
        >
          {t('common.by')}{' '}
          <span style={{ fontWeight: 600 }}>
            {author?.user_name ?? t('insights.unknown')}
          </span>
        </Typography>
      </View>

      <HSpacer grow />

      {open && (
        <HStack sx={{ gap: '12px' }}>
          {speakers.length > 1 && <PrimarySpeakerPicker />}
          <PotalInclusionPicker />
          <StatusPicker />
        </HStack>
      )}

      {hasPortalBadge && (
        <Typography
          variant="body2"
          sx={{
            ...generateBadgeStyles({
              color: ExclusionItemOptions['true'].color,
            }),
            mr: '1.25rem',
          }}
        >
          {t(`insights.not_in_portal`)}
        </Typography>
      )}

      {hasStatusBadge && entry.status && (
        <Typography
          variant="body2"
          sx={generateBadgeStyles({ color: StatusItems[entry.status].color })}
        >
          {t(`insights.highlight_status.${entry.status}`)}
        </Typography>
      )}
    </HStack>
  );
}

function Player() {
  const { open, padding, conversation, speakers, seekAudio } =
    useLocalContext();
  const { isLoading, isPlaying, audioError } = useContext(GlobalAudioContext);
  const { isActive, toggleActivatedPlaying } = seekAudio;

  return (
    <HStack
      sx={{
        gap: '16px',
        position: 'relative',
        margin: open
          ? `28px ${padding}px 24px`
          : `${padding}px ${padding}px 16px`,
      }}
    >
      <PlayButton
        isPlaying={isActive && isPlaying}
        isLoading={isActive && isLoading}
        isError={isActive && audioError != null}
        onClick={toggleActivatedPlaying}
        shape={'circle'}
        size="lg"
        style={{
          zIndex: 2,
          flexShrink: 0,
          flexBasis: `${open ? 60 : 48}px`,
          height: `${open ? 60 : 48}px`,
        }}
      />

      <VStack>
        <Typography sx={{ fontWeight: '700', fontSize: `${open ? 20 : 16}px` }}>
          {speakers.map((s) => s.name).join(', ')}
        </Typography>

        {conversation && (
          <Typography
            data-testid="conversation_title"
            sx={{ fontSize: `${open ? 20 : 16}px` }}
          >
            {conversation.title}
          </Typography>
        )}
      </VStack>
    </HStack>
  );
}

function ActionBar() {
  const {
    catalog,
    entry,
    conversations,
    padding,
    awaitingSuggestion,
    tagWithAI,
    aiOn,
  } = useLocalContext();

  const { selectedHighlightIds, t } = useHighlightsPageContext();
  const { codebookHasCodes } = useCatalogPageContext();
  const { analyticsEvent } = useAnalyticsContext();
  const conversationTitle =
    conversations.find((c) => c.id === entry.conversation_id)?.title ?? '';
  const downloadFilename = `Cortico ${conversationTitle} - highlight:${entry.annotation_id}.mp3`;
  const downloadHref = `${window.location.origin}/api/highlights/play/${entry.annotation_id}`;
  const handleDownload = () => {
    analyticsEvent({
      category: Category.Highlight,
      action: Action.Download,
      name: Name.HighlightCard,
    });
  };
  const tooManyHighlights = selectedHighlightIds.size > 20;

  return (
    <HStack sx={{ m: `${padding}px` }}>
      {aiOn && (
        <SuggestCodesTooltip
          placement="top-start"
          codebookHasCodes={codebookHasCodes}
          tooManyHighlights={tooManyHighlights}
        >
          <span>
            <Button
              tKey={
                awaitingSuggestion
                  ? 'insights.suggestingCodesWithAI'
                  : 'insights.suggestCodesWithAI'
              }
              disabled={!codebookHasCodes || tooManyHighlights}
              onClick={() => tagWithAI([entry.id])}
              busy={awaitingSuggestion}
            />
          </span>
        </SuggestCodesTooltip>
      )}
      <HSpacer grow />
      <Button
        component={'a'}
        href={downloadHref}
        onClick={handleDownload}
        //@ts-ignore - MUI doesn't adjust to the attributes of the component
        download={downloadFilename}
      >
        <FontAwesomeIcon icon={['far', 'arrow-to-bottom']} />
      </Button>
      {!catalog.mobile && (
        <LinkButton
          data-testid="ViewHighlightLink"
          tKey="insights.highlight_card_view_highlight"
          href={`/highlight/${entry.annotation_id}`}
          style={{ marginLeft: '12px' }}
        />
      )}
    </HStack>
  );
}

function PrimarySpeakerPicker() {
  const { speakers, entry, catalog, dispatch, t } = useLocalContext();

  const speakerItems = speakers.map((speaker) => ({
    ...speaker,
    label: speaker.name,
  }));
  const selectedSpeaker = speakerItems.find(
    (speaker) => speaker.id === entry.primary_participant_id
  );

  return (
    <Dropdown
      items={speakerItems}
      initialSelection={selectedSpeaker}
      placeholder={{ label: t('insights.unknown', 'Unknown') }}
      label="insights.highlight_card_primary_speaker"
      onSelect={(item) => {
        dispatch(assignPrimarySpeaker([catalog.id, entry.id, item.id]));
      }}
      style={{ zIndex: 2 }}
    />
  );
}

function StatusPicker() {
  const { entry, catalog, dispatch, t } = useLocalContext();
  const [selectedStatus, setSelectedStatus] = useState(entry.status);

  return (
    <Dropdown
      items={StatusItems}
      initialSelection={StatusItems.find((item) => item.value === entry.status)}
      placeholder={{ label: t('insights.highlight_status.to-do') }}
      label="common.status"
      onSelect={(item) => {
        setSelectedStatus(item.value);
        dispatch(setEntriesStatus([catalog.id, [entry.id], item.value]));
      }}
      color={selectedStatus && StatusItems[selectedStatus].color}
      style={{ zIndex: 10 }}
    />
  );
}

function PotalInclusionPicker() {
  const { entry, dispatch, t } = useLocalContext();
  const [excluded, setExcluded] = useState(entry.is_internal);

  return (
    <Dropdown
      items={ExclusionItems}
      initialSelection={ExclusionItems.find(
        (item) => item.value === entry.is_internal
      )}
      placeholder={{ label: t('insights.included') }}
      label="insights.portal"
      onSelect={(item) => {
        setExcluded(item.value);
        dispatch(markInternal([entry.id, item.value]));
      }}
      color={ExclusionItemOptions[excluded ? 'true' : 'false'].color}
      style={{ zIndex: 10 }}
    />
  );
}

// LOCAL CONTEXT

const [LocalProvider, useLocalContext] = createBasicProvider<
  {
    open: boolean;
    catalog: Catalog;
    entry: Entry;
    conversation?: Conversation;
    author: User;
    speakers: Participant[];
    checked: boolean;
    padding: number;
    seekAudio: ReturnType<typeof useSeekAudio>;
    awaitingSuggestion: boolean;
    onChangeCheck?: (checked: boolean, entry: Entry) => void;
  } & ReturnType<typeof useHighlightsPageContext>
>();

// LOCAL CONSTANTS

const ExclusionItemOptions: Record<
  string,
  {
    label: string;
    color: string;
    value: boolean;
  }
> = {
  true: { label: 'insights.excluded', value: true, color: color.red },
  false: { label: 'insights.included', value: false, color: color.tealDark },
};
const ExclusionItems = Object.assign(
  Object.values(ExclusionItemOptions),
  ExclusionItemOptions
);
